import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'

import { CWV_ROUTES, isDocumentPrototypeRoute } from '@sketch/modules-common'

import { useSearch } from '@sketch/utils'
import {
  MagIcon,
  SearchField,
  SearchButton,
  Wrapper,
} from './DocumentNavbar.styles'

const getPlaceholderByPath = (path: string) => {
  if (CWV_ROUTES.includes(path)) {
    return 'Search Components'
  } else if (isDocumentPrototypeRoute(path)) {
    return 'Search Prototypes'
  }

  return 'Search Frames'
}

interface DocumentNavbarProps
  extends Pick<React.ComponentProps<'input'>, 'onBlur' | 'onFocus'> {
  forceExpand?: boolean
  hidden?: boolean
}

const DocumentNavbar = (props: DocumentNavbarProps) => {
  const { path } = useRouteMatch()
  // Search query param, shared for both artboards and components
  const { search, setSearch: setSearchRaw } = useSearch()

  const [expanded, setExpanded] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const isInputExpanded = Boolean(
    props.forceExpand || expanded || search || isSearchFocused
  )

  const setSearch = useCallback(
    (value: string) => {
      const isInputEmpty = value.length === 0

      // Avoid search when value is empty and field is focused
      if (isInputEmpty && isSearchFocused) {
        return
      }

      setSearchRaw(value)
    },
    [isSearchFocused, setSearchRaw]
  )

  // When the users unfocuses the search input and it has no value
  // we clear the search value
  useEffect(() => {
    if (!isSearchFocused && isInputExpanded && inputRef.current?.value === '') {
      setSearchRaw('')
    }
  }, [isInputExpanded, isSearchFocused, setSearch, setSearchRaw])

  // Force focus on the input when it's mounted
  useEffect(() => {
    if (search || isSearchFocused) {
      inputRef.current?.focus()
    }
  }, [search, isSearchFocused])

  const onSearchButtonClick = useCallback(() => {
    setExpanded(true)
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!isInputExpanded) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.target instanceof HTMLElement &&
        inputRef.current?.contains(event.target)
      ) {
        return
      }

      setExpanded(false)
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [isInputExpanded])

  if (props.hidden) {
    return null
  }

  return (
    <Wrapper $isExpanded={isInputExpanded}>
      <SearchButton
        onClick={onSearchButtonClick}
        onFocus={() => {
          setIsSearchFocused(true)
        }}
        onBlur={() => {
          setIsSearchFocused(false)
        }}
        size="32"
        data-testid="search-toogle"
      >
        <MagIcon />
      </SearchButton>
      <SearchField
        ref={inputRef}
        placeholder={getPlaceholderByPath(path)}
        onChange={setSearch}
        value={search}
        onFocus={() => {
          setIsSearchFocused(true)
        }}
        onBlur={() => {
          setIsSearchFocused(false)
        }}
        {...props}
      />
    </Wrapper>
  )
}

export default DocumentNavbar
